'use client'

import { Check, Loader2 } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { type FormEvent, type KeyboardEvent, type ReactNode, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/widget/date-picker'
import {
  InstrumentChoiceCard,
  PositionChoiceCard,
} from '@/features/order/choice-picker/components/choice-cards'
import type {
  InstrumentChoiceData,
  PositionChoiceData,
} from '@/features/order/choice-picker/lib/choice-data'
import {
  type ChoiceOption,
  type ChoiceRequest,
  daysUntil,
  firstPickableDay,
  isoDateOf,
  isPickableDay,
  joinSelectedReplies,
  normalizeTypedAnswer,
  optionDates,
} from '@/features/order/choice-picker/lib/choice-request'
import { cn } from '@/lib/utils'

export interface ChoicePickerProps {
  request: ChoiceRequest
  /** Sends the answer as the next prompt of the same session. */
  onAnswer: (reply: string) => void
  /** An answer is on its way: everything is disabled and the sent option spins. */
  pending?: boolean
  /** Instrument card data by suggested symbol; null while it loads. */
  instrumentData?: Record<string, InstrumentChoiceData> | null
  /** Position card data by suggested symbol. */
  positionData?: Record<string, PositionChoiceData>
  /** Clock for "expires in N days"; injectable so fixtures render the same every time. */
  now?: number
  className?: string
}

/**
 * The answers to a question the trading backend asked instead of guessing. Instruments and
 * positions get rich cards (price, 24h, funding, greeks; side, size, uPnL) so the right one is
 * obvious at a glance; durations, amounts, prices and dates are chips. One answer sends on
 * pick; several are ticked and then confirmed. A typed answer is offered when the backend says
 * what format it takes. Keys: arrows move, Enter/Space pick, 1–9 pick directly.
 */
export function ChoicePicker({
  request,
  onAnswer,
  pending = false,
  instrumentData,
  positionData,
  now: nowProp,
  className,
}: ChoicePickerProps) {
  const t = useTranslations('orderForm.choicePicker')
  const { options, multiSelect, kind } = request
  const rich = kind === 'instrument' || kind === 'position'
  // Read at each use, not frozen at mount: a picker left open past midnight must not offer
  // yesterday.
  const clock = () => nowProp ?? Date.now()
  const now = clock()
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [sent, setSent] = useState<string | null>(null)
  const [focusIndex, setFocusIndex] = useState(0)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])

  const send = (reply: string) => {
    if (pending || !reply) return
    setSent(reply)
    onAnswer(reply)
  }
  const pick = (option: ChoiceOption) => {
    if (pending) return
    if (!multiSelect) return send(option.reply)
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(option.reply)) next.delete(option.reply)
      else next.add(option.reply)
      return next
    })
  }
  const confirm = () => send(joinSelectedReplies(options, selected))

  const moveFocus = (index: number) => {
    const next = (index + options.length) % options.length
    setFocusIndex(next)
    optionRefs.current[next]?.focus()
  }
  const onGroupKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return
    const current = optionRefs.current.findIndex((el) => el === document.activeElement)
    if (current === -1) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault()
      moveFocus(current + 1)
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault()
      moveFocus(current - 1)
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault()
      moveFocus(e.key === 'Home' ? 0 : options.length - 1)
    } else if (/^[1-9]$/.test(e.key) && Number(e.key) <= options.length) {
      e.preventDefault()
      moveFocus(Number(e.key) - 1)
      pick(options[Number(e.key) - 1])
    } else if (multiSelect && e.key === 'Enter' && selected.size > 0) {
      // Space ticks; Enter confirms what is ticked, like submitting a form.
      e.preventDefault()
      confirm()
    }
  }

  const isChecked = (option: ChoiceOption) =>
    multiSelect ? selected.has(option.reply) : sent === option.reply

  const renderOption = (option: ChoiceOption, index: number): ReactNode => {
    const checked = isChecked(option)
    const common = {
      ref: (el: HTMLButtonElement | null) => {
        optionRefs.current[index] = el
      },
      type: 'button' as const,
      role: multiSelect ? 'checkbox' : 'radio',
      'aria-checked': checked,
      'aria-keyshortcuts': index < 9 ? String(index + 1) : undefined,
      tabIndex: index === focusIndex ? 0 : -1,
      disabled: pending,
      onFocus: () => setFocusIndex(index),
      onClick: () => {
        setFocusIndex(index)
        pick(option)
      },
    }
    if (rich) {
      return (
        <button
          key={option.reply}
          {...common}
          className={cn(
            'group flex w-full cursor-pointer items-start gap-2.5 border px-3 py-2.5 text-left transition-colors disabled:cursor-default',
            checked
              ? 'border-foreground/60 bg-foreground/5'
              : 'border-foreground/10 not-disabled:hover:border-foreground/30 not-disabled:hover:bg-foreground/5',
            pending && !checked && 'opacity-50'
          )}
        >
          <Indicator
            index={index}
            checked={checked}
            multiSelect={multiSelect}
            spinning={pending && sent === option.reply && !multiSelect}
          />
          {kind === 'instrument' ? (
            <InstrumentChoiceCard
              symbol={option.reply}
              label={option.label}
              data={
                instrumentData === null
                  ? undefined
                  : (instrumentData?.[option.reply] ?? {
                      symbol: option.reply,
                    })
              }
              now={now}
            />
          ) : (
            <PositionChoiceCard
              symbol={option.reply}
              label={option.label}
              data={positionData?.[option.reply]}
            />
          )}
        </button>
      )
    }
    const days = kind === 'date' ? daysUntil(option.reply, now) : null
    return (
      <Button
        key={option.reply}
        {...common}
        variant={checked ? 'default' : 'outline'}
        size="sm"
        className={cn('px-3 text-xs', days !== null && 'h-auto flex-col gap-0 py-1.5')}
      >
        <span className="inline-flex items-center gap-1.5">
          {multiSelect && checked && <Check className="size-3" />}
          {pending && sent === option.reply && <Loader2 className="size-3 animate-spin" />}
          {kind === 'date' ? (
            <DateLabel date={option.reply} fallback={option.label} />
          ) : (
            option.label
          )}
        </span>
        {days !== null && (
          <span className={cn('text-[10px] font-normal', !checked && 'text-foreground/50')}>
            {t('inDays', { days })}
          </span>
        )}
      </Button>
    )
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {options.length > 0 ? (
        <>
          <div className="text-foreground/50 flex items-center justify-between text-[11px]">
            <span>{multiSelect ? t('pickMany') : t('pickOne')}</span>
            {options.length > 1 && (
              <span className="text-foreground/40">
                {t('keyHint', { last: Math.min(options.length, 9) })}
              </span>
            )}
          </div>
          <div
            role={multiSelect ? 'group' : 'radiogroup'}
            aria-label={t('answers')}
            onKeyDown={onGroupKeyDown}
            className={cn(rich ? 'flex flex-col gap-1.5' : 'flex flex-wrap gap-1.5')}
          >
            {options.map(renderOption)}
          </div>
          {multiSelect && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-foreground/50 text-[11px]">
                {t('selectedCount', { count: selected.size, total: options.length })}
              </span>
              <span className="flex items-center gap-1">
                {selected.size > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    disabled={pending}
                    onClick={() => setSelected(new Set())}
                  >
                    {t('clear')}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="default"
                  size="xs"
                  disabled={pending || selected.size === 0}
                  onClick={confirm}
                >
                  {pending && <Loader2 className="size-3 animate-spin" />}
                  {t('sendSelected', { count: selected.size })}
                </Button>
              </span>
            </div>
          )}
        </>
      ) : (
        <p className="text-foreground/50 text-[11px]">{t('empty')}</p>
      )}
      {request.input &&
        (kind === 'date' ? (
          <DateAnswer
            options={options}
            placeholder={request.input.placeholder}
            pending={pending}
            onSend={send}
            clock={clock}
            prompt={options.length > 0 ? t('orPickDate') : undefined}
          />
        ) : (
          <TypedAnswer
            request={request}
            pending={pending}
            onSend={send}
            prompt={options.length > 0 ? t('orType') : undefined}
          />
        ))}
    </div>
  )
}

/** The leading mark of a card: its number key, a tick once picked, a spinner while sending. */
function Indicator({
  index,
  checked,
  multiSelect,
  spinning,
}: {
  index: number
  checked: boolean
  multiSelect: boolean
  spinning: boolean
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'mt-1 grid size-4 shrink-0 place-content-center border text-[10px] leading-none transition-colors',
        !multiSelect && 'rounded-full',
        checked
          ? 'border-emphasis bg-emphasis text-emphasis-foreground'
          : 'border-foreground/35 text-foreground/50 group-hover:border-foreground/60'
      )}
    >
      {spinning ? (
        <Loader2 className="size-3 animate-spin" />
      ) : checked ? (
        <Check className="size-3" />
      ) : index < 9 ? (
        index + 1
      ) : null}
    </span>
  )
}

function DateLabel({ date, fallback }: { date: string; fallback: string }) {
  const locale = useLocale()
  const at = Date.parse(`${date}T00:00:00Z`)
  if (Number.isNaN(at)) return <>{fallback}</>
  return (
    <>
      {new Date(at).toLocaleDateString(locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      })}
    </>
  )
}

/**
 * A date answer picked on a calendar rather than typed, so it is always a real YYYY-MM-DD. Days
 * before today (UTC) are disabled; the suggested dates are marked (they stay chips above too).
 */
function DateAnswer({
  options,
  placeholder,
  pending,
  onSend,
  clock,
  prompt,
}: {
  options: ChoiceOption[]
  /** The backend's hint for the answer; the trigger reads it until a day is picked. */
  placeholder: string
  pending: boolean
  onSend: (reply: string) => void
  clock: () => number
  prompt?: string
}) {
  const t = useTranslations('orderForm.choicePicker')
  const suggested = optionDates(options)
  const today = firstPickableDay(clock())

  return (
    <div className="flex flex-col gap-1">
      {prompt && <span className="text-foreground/50 text-[11px]">{prompt}</span>}
      <DatePicker
        size="sm"
        emptyLabel={placeholder || t('pickDate')}
        minDate={today}
        // Re-checked when the calendar draws and when a day is picked, not only at render.
        disabled={(day) => !isPickableDay(day, clock())}
        defaultMonth={suggested.find((day) => day >= today) ?? today}
        modifiers={{ suggested }}
        modifiersClassNames={{ suggested: 'text-ox-cyan font-medium' }}
        triggerDisabled={pending}
        onSelect={(day) => {
          if (isPickableDay(day, clock())) onSend(isoDateOf(day))
        }}
        className="w-full justify-between"
      />
    </div>
  )
}

/** A free-form answer in the format the backend named, checked before it is sent. */
function TypedAnswer({
  request,
  pending,
  onSend,
  prompt,
}: {
  request: ChoiceRequest
  pending: boolean
  onSend: (reply: string) => void
  prompt?: string
}) {
  const t = useTranslations('orderForm.choicePicker')
  const [text, setText] = useState('')
  const [invalid, setInvalid] = useState(false)
  const input = request.input!

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const answer = normalizeTypedAnswer(input.kind, text, request.multiSelect)
    if (answer === null) {
      setInvalid(true)
      return
    }
    onSend(answer)
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-1">
      {prompt && <span className="text-foreground/50 text-[11px]">{prompt}</span>}
      <div className="flex items-center gap-1.5">
        <Input
          variant="outline"
          className="min-w-0 flex-1"
          inputClassName="h-8 px-3 text-xs"
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            setInvalid(false)
          }}
          placeholder={input.placeholder}
          aria-label={input.placeholder || t('typeAnswer')}
          aria-invalid={invalid}
          disabled={pending}
        />
        <Button
          type="submit"
          variant="secondary"
          size="sm"
          className="px-3 text-xs"
          disabled={pending || !text.trim()}
        >
          {t('send')}
        </Button>
      </div>
      {invalid && (
        <span role="alert" className="text-destructive text-[11px]">
          {t('invalid', { format: input.placeholder })}
        </span>
      )}
    </form>
  )
}
